import React from "react";
import moment from "moment";
import { FaPlus, FaRegClock, FaCheck, FaTimes } from "react-icons/fa";
import { DividerDiamond } from "components/Modals/BalanceModal";

const STATUS_STYLES = {
  Pending: "text-primary-400 bg-primary-400/10 border-primary-400/30",
  Approved: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",
  Rejected: "text-red-400 bg-red-400/10 border-red-400/30",
  Cancelled: "text-secondary-50 bg-secondary-400/40 border-primary-400",
};

const STATUS_ICON = {
  Pending: <FaRegClock />,
  Approved: <FaCheck />,
  Rejected: <FaTimes />,
  Cancelled: <FaTimes />,
};

const formatAmount = (value) =>
  (value ?? 0).toLocaleString("tr-TR", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });

const StatusBadge = ({ status, t, statusPrefix }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[9px] font-semibold uppercase tracking-wide whitespace-nowrap shrink-0 ${
      STATUS_STYLES[status] || STATUS_STYLES.Cancelled
    }`}
  >
    {STATUS_ICON[status] || null}
    {t(`${statusPrefix}${(status || "").toUpperCase()}`)}
  </span>
);

const CancelControl = ({ pending, cancelling, onCancel, t, cancelLabel, confirmLabel }) => {
  const [confirming, setConfirming] = React.useState(false);
  if (!pending) return <span className="text-secondary-500 text-xs">—</span>;

  if (confirming) {
    return (
      <div className="flex items-center gap-2 justify-end flex-wrap">
        <span className="text-white text-xs whitespace-nowrap">
          {confirmLabel}
        </span>
        <button
          type="button"
          onClick={onCancel}
          disabled={cancelling}
          className="rounded-full bg-red-500 hover:bg-red-400 disabled:opacity-50 text-white text-xs font-bold uppercase px-3 py-1.5 whitespace-nowrap transition-colors"
        >
          {cancelling ? (
            <span className="loading loading-spinner loading-xs" />
          ) : (
            t("TXT_YES")
          )}
        </button>
        <button
          type="button"
          onClick={() => setConfirming(false)}
          disabled={cancelling}
          className="rounded-full border border-secondary-500 text-white hover:bg-secondary-700/60 text-xs font-semibold uppercase px-3 py-1.5 whitespace-nowrap transition-colors"
        >
          {t("TXT_NO")}
        </button>
      </div>
    );
  }

  return (
    <div className="flex justify-end">
      <button
        type="button"
        onClick={() => setConfirming(true)}
        className="rounded-lg border border-red-500/50 bg-red-500/30 hover:bg-red-500/20 hover:border-red-500/70 text-red-400 text-xs font-bold uppercase tracking-wide px-3 py-1.5 whitespace-nowrap transition-colors"
      >
        {cancelLabel}
      </button>
    </div>
  );
};

const AmountDisplay = ({ item, currency, className = "text-base" }) => {
  const hasDiff =
    item.ApprovedAmount != null && item.ApprovedAmount !== item.__amount;
  if (!hasDiff) {
    return (
      <span className={`text-white font-bold whitespace-nowrap ${className}`}>
        {formatAmount(item.__amount)} {currency}
      </span>
    );
  }
  return (
    <div className="flex items-center gap-1.5 flex-wrap min-w-0">
      <span className="text-secondary-50 line-through text-xs whitespace-nowrap">
        {formatAmount(item.__amount)} {currency}
      </span>
      <span className={`text-emerald-400 font-bold whitespace-nowrap ${className}`}>
        {formatAmount(item.ApprovedAmount)} {currency}
      </span>
    </div>
  );
};

const RequestCard = ({ item, currency, onCancel, cancelling, t, statusPrefix, cancelLabel, confirmLabel, extraColumns }) => {
  const isPending = item.Status === "Pending";

  return (
    <div className="rounded-lg border border-secondary-400/40 bg-secondary-400/60 px-3.5 py-3 flex flex-col gap-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col gap-0.5 min-w-0">
          <AmountDisplay item={item} currency={currency} />
          <span className="text-white text-[11px]">
            {moment(item.__CreateAt).format("lll")}
          </span>
        </div>
        <StatusBadge status={item.Status} t={t} statusPrefix={statusPrefix} />
      </div>

      {extraColumns.map((col, idx) => {
        const content = col.mobileRender ? col.mobileRender(item) : null;
        if (!content) return null;
        return (
          <div key={idx} className="text-white text-sm">
            {content}
          </div>
        );
      })}

      {item.AdminNote && (
        <div className="rounded-md border border-secondary-50/50 border-dashed bg-secondary-50/10 px-2.5 py-2 flex flex-col gap-1">
          <span className="text-primary-50/70 text-[10px] uppercase tracking-wide font-semibold">
            {t("REQUEST_COLUMN_ADMIN_NOTE")}
          </span>
          <span className="text-white text-xs">{item.AdminNote}</span>
        </div>
      )}

      {isPending && (
        <CancelControl
          pending
          cancelling={cancelling}
          onCancel={() => onCancel(item.RequestID)}
          t={t}
          cancelLabel={cancelLabel}
          confirmLabel={confirmLabel}
        />
      )}
    </div>
  );
};

/**
 * Shared list layout for gamer-facing request pages (bonus / withdraw).
 * Renders a table on md+ screens and stacked cards on mobile so the
 * page doesn't collapse into a narrow column on wide viewports.
 */
const RequestsPage = ({
  t,
  icon: Icon,
  title,
  subtitle,
  requests,
  loading,
  currency,
  cancellingId,
  onCancel,
  onNewRequest,
  newButtonLabel,
  noDataLabel,
  cancelButtonLabel,
  cancelConfirmLabel,
  statusPrefix,
  getAmount,
  extraColumns = [],
}) => {
  const sorted = [...requests]
    .sort((a, b) => new Date(b.__CreateAt) - new Date(a.__CreateAt))
    .map((item) => ({ ...item, __amount: getAmount(item) }));

  return (
    <div className="h-full p-2">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 sm:py-4 bg-secondary-500/90 rounded-lg">
        <div className="flex flex-col">
          <h1 className="text-lg text-white font-semibold m-0">{title}</h1>
          <span className="text-secondary-50 text-xs mt-0.5">{subtitle}</span>
        </div>
        <button
          type="button"
          onClick={onNewRequest}
          className="flex items-center justify-center gap-1.5 rounded-full bg-gradient-to-r from-primary-500 via-primary-500 to-primary-500 text-white text-xs font-bold uppercase tracking-wide px-3.5 py-2 shadow-lg shadow-secondary-400/30 active:scale-[0.98] shrink-0"
        >
          <FaPlus className="w-3 h-3" />
          {newButtonLabel}
        </button>
      </div>

      <div
        style={{
          overflowY: "auto",
          height: `calc(100dvh - 262px)`,
          WebkitOverflowScrolling: "touch",
        }}
        className="mt-3 p-4 md:p-6 bg-secondary-500/70 rounded-lg w-full max-w-4xl mx-auto pb-8"
      >
        {loading && (
          <div className="flex justify-center py-14">
            <span className="loading loading-spinner text-primary-400" />
          </div>
        )}

        {!loading && sorted.length === 0 && (
          <div className="flex flex-col items-center gap-2 py-14 text-center rounded-2xl border-2 border-dashed border-secondary-700">
            <Icon className="text-primary-400/60 text-3xl" />
            <span className="italic text-white text-sm">{noDataLabel}</span>
          </div>
        )}

        {!loading && sorted.length > 0 && (
          <>
            <div className="hidden md:block">
              <div className="w-full mb-3">
                <DividerDiamond />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="text-secondary-50 text-[11px] uppercase tracking-wide border-b border-primary-400">
                      <th className="text-left font-semibold py-2 pr-3">
                        {t("REQUEST_COLUMN_DATE")}
                      </th>
                      <th className="text-left font-semibold py-2 pr-3">
                        {t("REQUEST_COLUMN_AMOUNT")}
                      </th>
                      {extraColumns.map((col, idx) => (
                        <th key={idx} className="text-left font-semibold py-2 pr-3">
                          {col.header}
                        </th>
                      ))}
                      <th className="text-left font-semibold py-2 pr-3">
                        {t("REQUEST_COLUMN_ADMIN_NOTE")}
                      </th>
                      <th className="text-left font-semibold py-2 pr-3">
                        {t("REQUEST_COLUMN_STATUS")}
                      </th>
                      <th className="text-right font-semibold py-2">
                        {t("REQUEST_COLUMN_ACTION")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-primary-400/60">
                    {sorted.map((item) => (
                      <tr key={item._id} className="hover:bg-secondary-700/20 transition-colors">
                        <td className="py-3 pr-3 text-white whitespace-nowrap">
                          {moment(item.__CreateAt).format("lll")}
                        </td>
                        <td className="py-3 pr-3">
                          <AmountDisplay
                            item={item}
                            currency={currency}
                            className="text-sm"
                          />
                        </td>
                        {extraColumns.map((col, idx) => (
                          <td key={idx} className="py-3 pr-3 text-white max-w-[220px] truncate">
                            {col.render(item)}
                          </td>
                        ))}
                        <td className="py-3 pr-3 max-w-[220px]">
                          {item.AdminNote ? (
                            <span
                              className="text-white text-xs truncate block"
                              title={item.AdminNote}
                            >
                              {item.AdminNote}
                            </span>
                          ) : (
                            <span className="text-secondary-500 text-xs">—</span>
                          )}
                        </td>
                        <td className="py-3 pr-3">
                          <StatusBadge status={item.Status} t={t} statusPrefix={statusPrefix} />
                        </td>
                        <td className="py-3">
                          <CancelControl
                            pending={item.Status === "Pending"}
                            cancelling={cancellingId === item._id}
                            onCancel={() => onCancel(item.RequestID)}
                            t={t}
                            cancelLabel={cancelButtonLabel}
                            confirmLabel={cancelConfirmLabel}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="md:hidden flex flex-col gap-2.5">
              {sorted.map((item) => (
                <RequestCard
                  key={item._id}
                  item={item}
                  currency={currency}
                  cancelling={cancellingId === item._id}
                  onCancel={onCancel}
                  t={t}
                  statusPrefix={statusPrefix}
                  cancelLabel={cancelButtonLabel}
                  confirmLabel={cancelConfirmLabel}
                  extraColumns={extraColumns}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RequestsPage;
